import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllAttributes, getAttributesForProduct } from '../firebase/attributes';
import {
    buildSubcategoryContext,
    calculateAttributePriceTotal,
    getProductSelectedAttributes,
    pickSelectionsForDefinitions,
    validateAttributeSelections,
} from '../utils/productAttributeSelection';

export function useProductAttributeForm(productData = {}) {
    const [allAttributes, setAllAttributes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [selections, setSelections] = useState(() => getProductSelectedAttributes(productData));

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getAllAttributes().then((result) => {
            if (!mounted) return;
            if (result.error) setFetchError(result.error);
            else setAllAttributes(result.attributes);
            setLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    const definitions = useMemo(() => {
        return getAttributesForProduct(
            allAttributes,
            productData.category,
            productData.categoryId ?? null,
            buildSubcategoryContext(productData),
        );
    }, [
        allAttributes,
        productData.category,
        productData.categoryId,
        productData.subcategory,
        productData.subcategoryId,
    ]);

    const setSelection = useCallback((name, value) => {
        setSelections((prev) => ({ ...prev, [name]: value }));
    }, []);

    const activeSelections = useMemo(
        () => pickSelectionsForDefinitions(definitions, selections),
        [definitions, selections],
    );

    const attributesPriceTotal = useMemo(
        () => calculateAttributePriceTotal(activeSelections),
        [activeSelections],
    );

    const validateAttributes = useCallback(
        () => validateAttributeSelections(definitions, activeSelections),
        [definitions, activeSelections],
    );

    const cartSelections = useMemo(() => {
        const cleaned = {};
        Object.entries(activeSelections).forEach(([name, value]) => {
            if (value === undefined || value === null || value === '') return;
            if (Array.isArray(value) && value.length === 0) return;
            if (typeof value === 'object' && value !== null && 'value' in value && !value.value) return;
            cleaned[name] = value;
        });
        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }, [activeSelections]);

    return {
        definitions,
        selections,
        setSelection,
        setSelections,
        loading,
        fetchError,
        attributesPriceTotal,
        validateAttributes,
        cartSelections,
    };
}
